<template>
  <aside v-if="items.length > 0" class="help-toc">
    <a-affix :offset-top="affixOffsetTop">
      <div class="help-toc__container">
        <h3 class="help-toc__title">目录</h3>
        <ul class="help-toc__list">
          <li
            v-for="(item, index) in items"
            :key="index"
            class="help-toc__item"
            :class="{
              'is-active': activeId === item.id,
              [`help-toc__item--level-${getRelativeLevel(item.level)}`]: true,
            }"
          >
            <a :href="`#${item.id}`" class="help-toc__link" @click.prevent="handleClick(item.id)">
              {{ item.text }}
            </a>
          </li>
        </ul>
      </div>
    </a-affix>

    <button class="help-toc__mobile-button" @click="drawerVisible = true">
      <icon-list />
    </button>

    <a-drawer
      v-model:visible="drawerVisible"
      :width="320"
      placement="right"
      :footer="false"
      :header="false"
      class="help-toc__drawer"
    >
      <div class="help-toc__drawer-content">
        <div class="help-toc__drawer-header">
          <h3 class="help-toc__drawer-title">目录</h3>
          <button class="help-toc__drawer-close" @click="drawerVisible = false">
            <icon-list />
          </button>
        </div>
        <ul class="help-toc__drawer-list">
          <li
            v-for="(item, index) in items"
            :key="index"
            class="help-toc__drawer-item"
            :class="{
              'is-active': activeId === item.id,
              [`help-toc__drawer-item--level-${getRelativeLevel(item.level)}`]: true,
            }"
          >
            <a
              :href="`#${item.id}`"
              class="help-toc__drawer-link"
              @click.prevent="handleDrawerClick(item.id)"
            >
              {{ item.text }}
            </a>
          </li>
        </ul>
      </div>
    </a-drawer>
  </aside>
</template>

<script setup>
/**
 * @description 目录导航组件
 * @param {Array<{id: string, text: string, level: number}>} items - 目录项数组
 * @emits click - 点击目录项时触发，传递锚点ID
 */

import { ref, computed, onMounted, onUnmounted } from 'vue'
import { IconList } from '@arco-design/web-vue/es/icon'

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  affixOffsetTop: {
    type: Number,
    default: 100,
  },
})

const emit = defineEmits(['click'])

const activeId = ref('')
const drawerVisible = ref(false)

/**
 * @description 将标题层级归一化为有效的 h1-h6 层级
 * @param {number|string} level - 原始标题层级
 * @returns {number} 有效标题层级
 */
const normalizeHeadingLevel = (level) => {
  const normalizedLevel = Number(level)
  return Number.isInteger(normalizedLevel) && normalizedLevel >= 1 && normalizedLevel <= 6
    ? normalizedLevel
    : 1
}

/**
 * @description 收集页面实际出现的标题层级，避免缺失中间层级时产生额外缩进
 */
const normalizedLevels = computed(() => {
  const levels = props.items.map((item) => normalizeHeadingLevel(item.level))
  return [...new Set(levels)].sort((left, right) => left - right)
})

/**
 * @description 按页面实际出现的标题层级计算连续的相对层级
 * @param {number|string} level - 原始标题层级
 * @returns {number} 用于目录缩进的连续层级
 */
const getRelativeLevel = (level) => {
  const levelIndex = normalizedLevels.value.indexOf(normalizeHeadingLevel(level))
  return levelIndex >= 0 ? levelIndex + 1 : 1
}

/**
 * @description 监听滚动，更新激活的目录项
 */
const updateActiveId = () => {
  if (!import.meta.client) return

  const headings = props.items
    .map((item) => {
      const element = document.getElementById(item.id)
      return { id: item.id, element }
    })
    .filter((item) => item.element)

  if (headings.length === 0) return

  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  const offset = 150

  for (let i = headings.length - 1; i >= 0; i--) {
    const { id, element } = headings[i]
    if (element && element.offsetTop <= scrollTop + offset) {
      activeId.value = id
      return
    }
  }

  activeId.value = headings[0]?.id || ''
}

onMounted(() => {
  if (import.meta.client) {
    updateActiveId()
    window.addEventListener('scroll', updateActiveId)
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    window.removeEventListener('scroll', updateActiveId)
  }
})

/**
 * @description 处理目录项点击
 * @param {string} anchorId - 锚点ID
 */
const handleClick = (anchorId) => {
  emit('click', anchorId)
}

/**
 * @description 处理抽屉内目录项点击
 * @param {string} anchorId - 锚点ID
 */
const handleDrawerClick = (anchorId) => {
  emit('click', anchorId)
  drawerVisible.value = false
}
</script>

<style lang="scss" scoped>
$help-toc-level-indent: $spacing-md;

.help-toc {
  align-self: start;

  @include respond-to-max('xl') {
    align-self: flex-end;
  }
}

.help-toc__container {
  background-color: transparent;
  padding: 32px 0 20px 24px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 24px;
    top: 32px;
    bottom: 20px;
    width: 1px;
    background-color: $border-light-color;
  }

  @include respond-to-max('xl') {
    display: none;
  }

  @include respond-to-max('lg') {
    max-height: calc(100vh - 100px);
  }
}

.help-toc__title {
  font-size: 14px;
  font-weight: 400;
  color: $text-content-color;
  margin: 0 0 0 6px;
  padding: 4px 8px;
  line-height: 22px;
  position: relative;
  z-index: 1;

  @include respond-to-max('lg') {
    font-size: 16px;
    margin-bottom: 12px;
  }
}

.help-toc__list {
  list-style: none;
  padding: 0;
  margin: 0 0 0 6px;
  position: relative;
  z-index: 1;
}

.help-toc__item {
  margin: 0;
  position: relative;
  min-height: 30px;

  &:last-child {
    margin-bottom: 0;
  }

  &.help-toc__item--level-1 {
    padding-left: 0;
  }

  &.help-toc__item--level-2 {
    padding-left: $help-toc-level-indent;
  }

  &.help-toc__item--level-3 {
    padding-left: $help-toc-level-indent * 2;
  }

  &.help-toc__item--level-4 {
    padding-left: $help-toc-level-indent * 3;
  }

  &.help-toc__item--level-5 {
    padding-left: $help-toc-level-indent * 4;
  }

  &.help-toc__item--level-6 {
    padding-left: $help-toc-level-indent * 5;
  }

  &.is-active {
    .help-toc__link {
      color: $text-title-color;
      font-weight: 500;
    }

    &::before {
      content: '';
      position: absolute;
      left: -6px;
      top: 50%;
      width: 2px;
      height: 16px;
      background-color: $primary-color;
      border-radius: 0;
      transform: translateY(-50%);
      z-index: 1;
    }
  }
}

.help-toc__link {
  display: block;
  font-size: 14px;
  color: $text-content-color;
  text-decoration: none;
  transition: color 0.3s;
  line-height: 22px;
  padding: 4px 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: $primary-color;
  }

  @include respond-to-max('lg') {
    font-size: 13px;
  }
}

.help-toc__mobile-button {
  display: none;

  @include respond-to-max('xl') {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background-color: $bg-white;
    border: 1px solid $border-dashed-color;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
    color: $text-title-color;
    padding: 0;

    &:hover {
      border-color: $primary-color;
      color: $primary-color;
      background-color: rgba($primary-color, 0.05);
    }

    &:active {
      background-color: rgba($primary-color, 0.1);
    }
  }

  @include respond-to-max('lg') {
    width: 36px;
    height: 36px;
  }
}

.help-toc__drawer {
  :deep(.arco-drawer-body) {
    padding: 0;
  }
}

.help-toc__drawer-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.help-toc__drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid $border-dashed-color;

  @include respond-to-max('lg') {
    padding: 16px 20px;
  }
}

.help-toc__drawer-title {
  font-size: 18px;
  font-weight: 500;
  color: $text-title-color;
  margin: 0;

  @include respond-to-max('lg') {
    font-size: 16px;
  }
}

.help-toc__drawer-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  cursor: pointer;
  color: $text-content-color;
  transition: color 0.3s;
  padding: 0;
  border-radius: 4px;

  &:hover {
    color: $primary-color;
    background-color: rgba($primary-color, 0.05);
  }

  @include respond-to-max('lg') {
    width: 28px;
    height: 28px;
  }
}

.help-toc__drawer-list {
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  flex: 1;
  padding: 16px 24px;

  @include respond-to-max('lg') {
    padding: 12px 20px;
  }
}

.help-toc__drawer-item {
  margin: 0 0 12px 0;

  &:last-child {
    margin-bottom: 0;
  }

  &.help-toc__drawer-item--level-1 {
    padding-left: 0;
  }

  &.help-toc__drawer-item--level-2 {
    padding-left: $help-toc-level-indent;
  }

  &.help-toc__drawer-item--level-3 {
    padding-left: $help-toc-level-indent * 2;
  }

  &.help-toc__drawer-item--level-4 {
    padding-left: $help-toc-level-indent * 3;
  }

  &.help-toc__drawer-item--level-5 {
    padding-left: $help-toc-level-indent * 4;
  }

  &.help-toc__drawer-item--level-6 {
    padding-left: $help-toc-level-indent * 5;
  }

  &.is-active {
    .help-toc__drawer-link {
      color: $primary-color;
      font-weight: 500;
    }
  }
}

.help-toc__drawer-link {
  display: block;
  font-size: 14px;
  color: $text-content-color;
  text-decoration: none;
  transition: color 0.3s;
  line-height: 1.5;

  &:hover {
    color: $primary-color;
  }

  @include respond-to-max('lg') {
    font-size: 13px;
  }
}
</style>
